import type {
  RecordListItemDTO,
  AdminRecordListItemDTO,
  PaginatedRecordsResponseDTO,
  UpdateRecordsRequestDTO,
  RecordsFiltersDTO,
} from "@/application/records/dtos/RecordListItemDTO";
import type { RecordsGatewayPort, MarkdownEndpoint } from "@/application/records/ports/RecordsGatewayPort";
import type {
  RecordBatchMetaResponse,
  RecordMetaResponse,
  DetailedReportResponse,
} from "@/lib/types";
import type { RecordsMetaBatchRequestOptions } from "@/lib/records-meta-batch";
import type { RecordDetailRequestContext } from "@/lib/record-detail-transport";

export class RecordsApplicationService {
  constructor(private readonly gateway: RecordsGatewayPort) {}

  async getRecordsList(limit?: number): Promise<RecordListItemDTO[]> {
    return this.gateway.getRecordsList(limit);
  }

  async getAdminRecordsList(
    page?: number,
    limit?: number,
    filters?: RecordsFiltersDTO
  ): Promise<AdminRecordListItemDTO[]> {
    return this.gateway.getAdminRecordsList(page, limit, filters);
  }

  async getRecordsPaginated(
    page: number,
    limit: number,
    filters?: RecordsFiltersDTO
  ): Promise<PaginatedRecordsResponseDTO> {
    return this.gateway.getRecordsPaginated(page, limit, filters);
  }

  async getAdminRecordsPaginated(
    page: number,
    limit: number,
    filters?: RecordsFiltersDTO
  ): Promise<PaginatedRecordsResponseDTO> {
    return this.gateway.getAdminRecordsPaginated(page, limit, filters);
  }

  async updateRecords(request: UpdateRecordsRequestDTO): Promise<void> {
    await this.gateway.updateRecords(request);
  }

  async getRecordMeta(
    recordId: string,
    include?: string[]
  ): Promise<RecordMetaResponse> {
    return this.gateway.getRecordMeta(recordId, include);
  }

  async getRecordsMetaBatch(
    recordIds: string[],
    options?: RecordsMetaBatchRequestOptions
  ): Promise<Record<string, RecordBatchMetaResponse>> {
    return this.gateway.getRecordsMetaBatch(recordIds, options);
  }

  async getDetailedRecord(
    recordId: string,
    version?: number | null,
    requestContext?: RecordDetailRequestContext
  ): Promise<DetailedReportResponse> {
    return this.gateway.getDetailedRecord(recordId, version, requestContext);
  }

  async getMarkdownReport(
    recordId: string,
    endpoint: MarkdownEndpoint,
    version?: string | number | null
  ) {
    return this.gateway.getMarkdownReport(recordId, endpoint, { version });
  }

  async updateMarkdownReport(
    recordId: string,
    endpoint: MarkdownEndpoint,
    content: string,
    version?: number | null
  ) {
    await this.gateway.updateMarkdownReport(recordId, endpoint, content, version);
  }

  async patchTranscriptUtterance(
    recordId: string,
    utteranceId: number,
    newText: string,
    version?: number | null
  ) {
    await this.gateway.patchTranscriptUtterance(
      recordId,
      utteranceId,
      newText,
      version
    );
  }

  async patchSpeakerName(
    recordId: string,
    speaker: string,
    speakerName: string,
    version?: number | null
  ) {
    await this.gateway.patchSpeakerName(
      recordId,
      speaker,
      speakerName,
      version
    );
  }

  async patchUtteranceSpeaker(
    recordId: string,
    utteranceId: number,
    newSpeaker: string,
    version?: number | null
  ) {
    await this.gateway.patchUtteranceSpeaker(
      recordId,
      utteranceId,
      newSpeaker,
      version
    );
  }

  async patchSpeakerRespondent(
    recordId: string,
    speaker: string,
    isRespondent: boolean,
    version?: number | null
  ) {
    await this.gateway.patchSpeakerRespondent(
      recordId,
      speaker,
      isRespondent,
      version
    );
  }

  async switchPublicity(recordId: string, isPublic: boolean): Promise<void> {
    await this.gateway.switchPublicity(recordId, isPublic);
  }
}
